import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import { Reveal, MaskedLines, Marquee } from "../components/Reveal";
import { ButtonLink } from "../components/Buttons";
import CTASection from "../components/CTASection";
import GalleryGrid from "../components/GalleryGrid";
import { site } from "../config/site";
import { galleryImages } from "../data/gallery";

const storyImg =
  "https://images.unsplash.com/photo-1585747860715-2ba37e788b70?auto=format&fit=crop&w=1200&q=80";
const detailImg =
  "https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?auto=format&fit=crop&w=1000&q=80";

const chapters = [
  {
    n: "01",
    title: "Understanding, First",
    copy: "Every visit starts with a conversation. We listen to what you want before we begin — your style, your comfort, your routine.",
  },
  {
    n: "02",
    title: "A Unisex Space",
    copy: "Our salon welcomes everyone — a contemporary, comfortable environment designed for all genders and all styles.",
  },
  {
    n: "03",
    title: "Effortless, Every Visit",
    copy: "From a fresh haircut to a complete beauty refresh, every visit should feel easy, comfortable and personal.",
  },
];

export default function AboutPage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <>
      <SEO
        title="About Glow & Blow Unisex Salon | Banjara Hills"
        description="About Glow & Blow Unisex Salon in Banjara Hills, Hyderabad — a contemporary unisex salon for hair, beauty and grooming."
      />
      <PageHero
        eyebrow="About Glow & Blow"
        titleLines={[
          <>Beauty That</>,
          <>
            Feels <em className="italic text-champagne">Personal</em>
          </>,
        ]}
        copy="At Glow & Blow, every visit starts with understanding what you want. Whether you're looking for a fresh haircut, a new style or a beauty refresh, our goal is to make your salon experience comfortable, personal and effortless."
      />

      {/* Story */}
      <section className="pb-24 md:pb-32" data-testid="about-story">
        <div className="container-x grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="img-frame">
                <img
                  src={storyImg}
                  alt="Inside the Glow & Blow salon space in Banjara Hills"
                  loading="lazy"
                  width="1000"
                  height="750"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:pl-8">
            <SectionHeading
              index="01"
              eyebrow="Our Story"
              title={
                <>
                  A Contemporary Salon, <em className="italic text-champagne">A Personal Touch</em>
                </>
              }
            />
            <Reveal delay={0.2}>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-soften md:text-lg">
                <p>
                  Glow &amp; Blow is a unisex salon built around a simple idea:
                  great hair and beauty care should feel personal, unhurried and
                  welcoming — for everyone.
                </p>
                <p>
                  Located in Banjara Hills, Hyderabad, our unisex salon welcomes
                  clients looking for professional grooming and beauty services
                  in a contemporary salon environment.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-9">
                <ButtonLink to="/book" data-testid="about-book-btn">
                  Book an Appointment
                </ButtonLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee dark items={["Personal", "Unisex", "Professional", "Welcoming"]} />

      {/* Manifesto chapters */}
      <section className="py-24 md:py-32" data-testid="about-manifesto">
        <div className="container-x">
          <SectionHeading
            index="02"
            eyebrow="Our Philosophy"
            title={
              <>
                How We <em className="italic text-champagne">Work</em>
              </>
            }
            className="mb-14"
          />
          <div className="border-t border-line">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div
                  className="grid gap-4 border-b border-line py-10 md:grid-cols-12 md:gap-8 md:py-12"
                  data-testid={`about-chapter-${i}`}
                >
                  <span className="font-display text-4xl italic text-champagne md:col-span-2">
                    {c.n}
                  </span>
                  <h3 className="font-display text-2xl font-medium text-ink md:col-span-4 md:text-3xl">
                    {c.title}
                  </h3>
                  <p className="text-base leading-relaxed text-soften md:col-span-6 md:text-lg">
                    {c.copy}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Environment */}
      <section
        ref={ref}
        className="grain-dark relative overflow-hidden bg-ink py-24 md:py-32"
        data-testid="about-environment"
      >
        <div className="container-x grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6 lg:pr-8">
            <Reveal>
              <div className="flex items-center gap-4">
                <p className="eyebrow-light">The Space</p>
                <span className="h-px w-12 bg-champagne/50" aria-hidden="true" />
              </div>
            </Reveal>
            <h2 className="mt-6 font-display text-[clamp(2rem,4vw,3.4rem)] font-medium leading-[1.05] text-ivory">
              <MaskedLines
                lines={[
                  <>Designed to</>,
                  <>
                    <em className="italic text-champagne">Slow You Down</em>
                  </>,
                ]}
              />
            </h2>
            <Reveal delay={0.25}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-ivory/70 md:text-lg">
                Warm light, considered details and a calm atmosphere — the salon
                is designed so your time here feels like a pause in the day, not
                an errand.
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <p className="mt-6 border-l-2 border-champagne pl-5 font-display text-xl italic leading-relaxed text-ivory/85 md:text-2xl">
                “Located in Banjara Hills, Hyderabad — {site.hoursDays.toLowerCase()},{" "}
                {site.hoursTime}.”
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal>
              <div className="overflow-hidden">
                <motion.img
                  src={detailImg}
                  alt="Salon interior details at Glow & Blow Unisex Salon"
                  loading="lazy"
                  width="900"
                  height="1100"
                  style={{ y, scale: 1.12 }}
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Gallery strip */}
      <section className="py-24 md:py-32" data-testid="about-gallery">
        <div className="container-x">
          <SectionHeading
            index="03"
            eyebrow="The Salon, In Frames"
            title={
              <>
                Moments From <em className="italic text-champagne">The Studio</em>
              </>
            }
            className="mb-14"
          />
          <GalleryGrid items={galleryImages.slice(6, 12)} />
        </div>
      </section>

      <CTASection />
    </>
  );
}
